import { handleActions } from '@housinganywhere/safe-redux';
import {
  AuthModalActionTypes,
  AuthModalActionsType,
} from 'ha/modules/AuthModal/actions';
import { ImpersonationInfo } from '@ha/api/v2/types/UserInformation';
import { User } from '@ha/api/v2/types/User';

import {
  AuthLogicTypes,
  FetchUserBillingInfoTypes,
  RecoverPasswordTypes,
  ProfileEditActionTypes,
} from './constants';
import { AuthLogicActions } from './actions';
import { ActionsType as FetchUserBillingInfoActions } from './actions/fetchUserBillingInfo';
import { UserInfoResponse } from './types';

export interface AuthLogicState {
  isLoading: boolean;
  isLoaded: boolean;
  user: User | null;
  userInformation: UserInfoResponse | null;
  language: string;
  isAuthenticated: boolean;
  impersonatingAdmin: ImpersonationInfo | null;
  isUserBillingInfoLoading: boolean;
}

export const initialState = {
  isLoading: false,
  isLoaded: false,
  user: null,
  userInformation: null,
  language: 'en',
  isAuthenticated: false,
  impersonatingAdmin: null,
  isUserBillingInfoLoading: false,
};

const initUser = (
  state: AuthLogicState,
  payload: {
    user: User;
    userInformation: UserInfoResponse;
  },
) => {
  const { user, userInformation } = payload;

  return {
    ...state,
    user: {
      ...state.user,
      ...user,
    },
    userInformation: {
      ...state.userInformation,
      ...userInformation,
    },
    isAuthenticated: true,
    isLoading: false,
    isLoaded: true,
  };
};

type ActionTypes =
  | AuthLogicTypes
  | AuthModalActionTypes
  | FetchUserBillingInfoTypes
  | ProfileEditActionTypes.updateUser
  | RecoverPasswordTypes;

type Actions =
  | AuthLogicActions
  | AuthModalActionsType
  | FetchUserBillingInfoActions;

const reducer = handleActions<AuthLogicState, ActionTypes, Actions>(
  {
    [AuthLogicTypes.authStart]: s => ({
      ...s,
      isLoading: true,
    }),
    [AuthLogicTypes.authFail]: s => ({
      ...s,
      isLoading: false,
      isLoaded: true,
    }),
    [AuthLogicTypes.authDone]: s => ({
      ...s,
      isLoading: false,
      isLoaded: true,
    }),
    [AuthLogicTypes.fulfillImpersonation]: (s, action) => ({
      ...s,
      impersonatingAdmin: action.payload,
    }),
    [AuthLogicTypes.initUser]: (s, action) => initUser(s, action.payload),
    [AuthLogicTypes.login]: (s, action) => initUser(s, action.payload),
    // action is used only for analytics at the moment
    [AuthLogicTypes.logoutStart]: s => s,
    [AuthLogicTypes.logoutDone]: s => ({
      ...s,
      isAuthenticated: false,
      isLoaded: false,
      user: null,
      userInformation: null,
      impersonatingAdmin: null,
    }),
    [AuthLogicTypes.updateUser]: (s, action) => ({
      ...s,
      user: {
        ...(s.user as User),
        ...action.payload,
      },
    }),
    [AuthModalActionTypes.changeModalType]: () => initialState,
    [FetchUserBillingInfoTypes.start]: s => ({
      ...s,
      isUserBillingInfoLoading: true,
    }),
    [FetchUserBillingInfoTypes.fail]: s => ({
      ...s,
      isUserBillingInfoLoading: false,
    }),
    [FetchUserBillingInfoTypes.done]: (s, action) => {
      return {
        ...s,
        userInformation: {
          ...s.userInformation,
          ...action.payload,
        },
        isUserBillingInfoLoading: false,
      };
    },
    [ProfileEditActionTypes.updateUser]: (
      s,
      {
        payload,
      }: {
        type: string;
        payload: User;
      },
    ) => ({
      ...s,
      user: s.user && {
        ...s.user,
        ...payload,
      },
    }),
    [RecoverPasswordTypes.start]: s => ({
      ...s,
      isLoading: true,
    }),
    [RecoverPasswordTypes.fail]: s => ({
      ...s,
      isLoading: false,
    }),
    [RecoverPasswordTypes.done]: s => ({
      ...s,
      isLoading: false,
    }),
  },
  initialState,
);

export { reducer };
